import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  MenuItem,
  IconButton,
  Typography,
  CircularProgress,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import useUpdate from "../../api/useUpdate";
import useFetchObjects from "../../api/useFetchObjects";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import { getUniqueCurrencies } from "../../utils/currencyOptions";
import {
  getFormDirection,
  getRtlSelectProps,
  getRtlTextFieldProps,
} from "../utils/rtlForm";

import JalaliDateField from "../Components/JalaliDateField";
import { formatProductWithSize } from "../utils/productDisplay";
const belongsToShop = (item, shopId) =>
  !shopId || String(item?.shop ?? item?.shop_details?.id ?? "") === String(shopId);

const PurchaseEdit = ({ open, onClose, editData, token }) => {
  const { t, i18n } = useTranslation();
  const formDirection = getFormDirection(i18n);
  const { fieldDirection, fieldTextAlign } = formDirection;
  const selectProps = getRtlSelectProps(formDirection);
  const { handleUpdate, loading, isSuccess } = useUpdate(
    "shop/purchases",
    token
  );

  const { data: shopsData, loading: shopLoading } = useFetchObjects(
    ["shop"],
    "shop/shop?page_size=all",
    token
  );
  const { data: suppliersData, loading: supplierLoading } = useFetchObjects(
    ["supplier"],
    "shop/suppliers?page_size=all",
    token
  );
  const { data: productsData, loading: productLoading } = useFetchObjects(
    ["product"],
    "shop/products?page_size=all",
    token
  );
  const { data: unitsData, loading: unitLoading } = useFetchObjects(
    ["unit"],
    "shop/units?page_size=all",
    token
  );
  const { data: currenciesData, loading: currencyLoading } = useFetchObjects(
    ["currency"],
    "shop/currencies?page_size=all",
    token
  );

  const shops = Array.isArray(shopsData) ? shopsData : shopsData?.results || [];
  const suppliers = Array.isArray(suppliersData) ? suppliersData : suppliersData?.results || [];
  const products = Array.isArray(productsData) ? productsData : productsData?.results || [];
  const units = Array.isArray(unitsData) ? unitsData : unitsData?.results || [];
  const currencies = Array.isArray(currenciesData) ? currenciesData : currenciesData?.results || [];

  const [shop, setShop] = useState("");
  const [supplier, setSupplier] = useState("");
  const [discount, setDiscount] = useState("");
  const [receivedAmount, setReceivedAmount] = useState("");
  const [purchaseCurrency, setPurchaseCurrency] = useState("");
  const [purchaseDate, setPurchaseDate] = useState("");
  const [items, setItems] = useState([]);

  const branchSuppliers = useMemo(
    () => suppliers.filter((item) => belongsToShop(item, shop)),
    [suppliers, shop]
  );
  const branchProducts = useMemo(
    () => products.filter((item) => belongsToShop(item, shop)),
    [products, shop]
  );
  const branchUnits = useMemo(
    () => units.filter((item) => belongsToShop(item, shop)),
    [units, shop]
  );
  const branchCurrencies = useMemo(
    () => getUniqueCurrencies(currencies.filter((item) => belongsToShop(item, shop))),
    [currencies, shop]
  );

  useEffect(() => {
    if (editData) {
      setShop(editData.shop ? String(editData.shop) : "");
      setSupplier(editData.supplier ? String(editData.supplier) : "");
      setDiscount(editData.amount || "");
      
      setReceivedAmount(editData.received_amount || "");
      setPurchaseCurrency(editData.currency ? String(editData.currency) : "");
      setPurchaseDate(
        editData.purchase_date ? editData.purchase_date.substring(0, 16) : ""
      );
      if (editData.purchase_items) {
        const initialItems = editData.purchase_items.map((item) => ({
          id: item.id,
          product: item.product ? String(item.product) : "",
          unit: item.unit ? String(item.unit) : "",
          quantity: item.quantity,
          buy_price: item.buy_price,
          wholesale_price: item.wholesale_price || "",
          retail_price: item.retail_price || item.sale_price || "",
        }));
        setItems(initialItems);
      }
    }
  }, [editData]);

  const handleShopChange = (value) => {
    setShop(value);
    setSupplier("");
    setPurchaseCurrency("");
    setItems([]);
  };

  const addItem = () => {
    setItems([
      ...items,
      {
        product: "",
        unit: "",
        quantity: "",
        buy_price: "",
        wholesale_price: "",
        retail_price: "",
      },
    ]);
  };

  const removeItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };

  const totalAmount = items.reduce((acc, item) => {
    const qty = parseFloat(item.quantity) || 0;
    const price = parseFloat(item.buy_price) || 0;
    return acc + qty * price;
  }, 0);

  const discountValue = parseFloat(discount) || 0;
  const finalTotal = totalAmount - discountValue;

  const parsedReceived = parseFloat(receivedAmount) || 0;
  const receivedValid = parsedReceived <= finalTotal;

  const handleSubmit = () => {
    const payload = {
      shop,
      supplier,
      amount: discountValue,
      received_amount: receivedAmount,
      currency: purchaseCurrency,
      purchase_date: purchaseDate,
      items,
    };
    handleUpdate(editData.id, payload);
  };

  useEffect(() => {
    if (isSuccess) {
      setShop("");
      setSupplier("");
      setDiscount("");
      setReceivedAmount("");
      setPurchaseCurrency("");
      setPurchaseDate("");
      setItems([]);
      onClose();
    }
  }, [isSuccess, onClose]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg" dir={fieldDirection}>
      <DialogTitle>{t("purchase.editPurchase")}</DialogTitle>
      <DialogContent>
        <Box
          component="form"
          noValidate
          sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 2, direction: fieldDirection }}
        >
          <TextField
            select
            label={t("purchase.selectShop")}
            fullWidth
            required
            value={shop}
            onChange={(e) => handleShopChange(e.target.value)}
            disabled={shopLoading}
            SelectProps={selectProps}
            {...getRtlTextFieldProps(formDirection)}
          >
            {(shops || []).map((s) => (
              <MenuItem key={s.id} value={String(s.id)}>
                {s.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label={t("purchase.selectSupplier")}
            fullWidth
            required
            value={supplier}
            onChange={(e) => setSupplier(e.target.value)}
            disabled={supplierLoading || !shop}
            SelectProps={selectProps}
            {...getRtlTextFieldProps(formDirection)}
          >
            {(branchSuppliers || []).map((sup) => (
              <MenuItem key={sup.id} value={String(sup.id)}>
                {sup.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label={t("purchase.selectCurrency")}
            fullWidth
            required
            value={purchaseCurrency}
            onChange={(e) => setPurchaseCurrency(e.target.value)}
            disabled={currencyLoading || !shop}
            SelectProps={selectProps}
            {...getRtlTextFieldProps(formDirection)}
          >
            {(branchCurrencies || []).map((curr) => (
              <MenuItem key={curr.id} value={String(curr.id)}>
                {curr.title}
              </MenuItem>
            ))}
          </TextField>
          <JalaliDateField
            mode="date"
            label={t("purchase.purchaseDate")}
            fullWidth
            required
            value={purchaseDate}
            onChange={(e) => setPurchaseDate(e.target.value)}
            {...getRtlTextFieldProps(formDirection, { InputLabelProps: { shrink: true } })}
          />
          <TextField
            label={t("purchase.discount")}
            type="number"
            fullWidth
            value={discount}
            onChange={(e) => setDiscount(e.target.value)}
            {...getRtlTextFieldProps(formDirection)}
          />
          <Box sx={{ mt: 2, direction: fieldDirection }}>
            <Typography variant="h6" sx={{ textAlign: fieldTextAlign }}>{t("purchase.purchaseItems")}</Typography>
            {items.map((item, index) => (
              <Box
                key={index}
                sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1, direction: fieldDirection }}
              >
                <TextField
                  select
                  label={t("purchase.selectProduct")}
                  required
                  value={item.product}
                  onChange={(e) =>
                    handleItemChange(index, "product", e.target.value)
                  }
                  disabled={productLoading || !shop}
                  SelectProps={selectProps}
                  {...getRtlTextFieldProps(formDirection, { sx: { flex: 1 } })}
                >
                  {(branchProducts || []).map((prod) => (
                    <MenuItem key={prod.id} value={String(prod.id)}>
                      {formatProductWithSize(prod)}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  label={t("purchase.selectUnit")}
                  required
                  value={item.unit}
                  onChange={(e) =>
                    handleItemChange(index, "unit", e.target.value)
                  }
                  disabled={unitLoading || !shop}
                  SelectProps={selectProps}
                  {...getRtlTextFieldProps(formDirection, { sx: { flex: 1 } })}
                >
                  {(branchUnits || []).map((unit) => (
                    <MenuItem key={unit.id} value={String(unit.id)}>
                      {unit.title}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  label={t("purchase.quantity")}
                  type="number"
                  required
                  value={item.quantity}
                  onChange={(e) =>
                    handleItemChange(index, "quantity", e.target.value)
                  }
                  {...getRtlTextFieldProps(formDirection, { sx: { flex: 1 } })}
                />
                <TextField
                  label={t("purchase.buyPrice")}
                  type="number"
                  required
                  value={item.buy_price}
                  onChange={(e) =>
                    handleItemChange(index, "buy_price", e.target.value)
                  }
                  {...getRtlTextFieldProps(formDirection, { sx: { flex: 1 } })}
                />
                <TextField
                  label={t("purchase.wholesalePrice", "Wholesale Price")}
                  type="number"
                  value={item.wholesale_price}
                  onChange={(e) =>
                    handleItemChange(index, "wholesale_price", e.target.value)
                  }
                  {...getRtlTextFieldProps(formDirection, { sx: { flex: 1 } })}
                />
                <TextField
                  label={t("purchase.retailPrice", "Retail Price")}
                  type="number"
                  required
                  value={item.retail_price}
                  onChange={(e) =>
                    handleItemChange(index, "retail_price", e.target.value)
                  }
                  {...getRtlTextFieldProps(formDirection, { sx: { flex: 1 } })}
                />
                <IconButton onClick={() => removeItem(index)} color="error">
                  <RemoveCircleOutlineIcon />
                </IconButton>
              </Box>
            ))}
            <Button
              variant="outlined"
              startIcon={<AddCircleOutlineIcon />}
              onClick={addItem}
              sx={{ mt: 1 }}
            >
              {t("purchase.addItem")}
            </Button>
          </Box>
          <Box sx={{ mt: 2, textAlign: fieldTextAlign }}>
            <Typography variant="body1">
              {t("purchase.subtotal")}: {totalAmount.toFixed(2)}
            </Typography>
            {discountValue > 0 && (
              <Typography variant="body1">
                {t("purchase.discountAmount")}: -{discountValue.toFixed(2)}
              </Typography>
            )}
            <Typography variant="h6">
              {t("purchase.totalAmount")}: {finalTotal.toFixed(2)}
            </Typography>
          </Box>
          <TextField
            label={t("purchase.receivedAmount")}
            type="number"
            fullWidth
            value={receivedAmount}
            onChange={(e) => setReceivedAmount(e.target.value)}
            error={!receivedValid}
            helperText={
              !receivedValid ? t("purchase.receivedExceedsTotal") : ""
            }
            {...getRtlTextFieldProps(formDirection)}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={loading}>
          {t("purchase.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={
            loading ||
            !shop ||
            !supplier ||
            !purchaseCurrency ||
            items.length === 0 ||
            !receivedValid
          }
          sx={{ minWidth: 100 }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t("purchase.submit")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PurchaseEdit;
